import React, { useState, useEffect } from 'react';
import { Button, Card, Rate, Avatar, Typography, Space, Row, Col, Form, Input, message, Carousel } from 'antd';
import {
    CheckCircleOutlined,
    StarOutlined,
    RocketOutlined,
    MobileOutlined,
    UserOutlined,
    CrownOutlined,
    FireOutlined,
    DownloadOutlined,
    WifiOutlined,
    TeamOutlined,
    FileTextOutlined,
    SendOutlined,
    SafetyOutlined,
    CloudDownloadOutlined
} from '@ant-design/icons';
import InspectionBtn from '@/components/InspectionBtn';
import toast from "react-hot-toast";
import { useNavigate } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const SpotFlashLanding = () => {
    const [hoveredPlan, setHoveredPlan] = useState(null);
    const [feedbackForm] = Form.useForm();
    const [currentTestimonial, setCurrentTestimonial] = useState(0);
    const navigate = useNavigate()
    const features = [
        {
            icon: <MobileOutlined className="text-3xl text-[#289E80]" />,
            title: "Mobile-First Design",
            description: "Conduct inspections seamlessly on any device with our responsive mobile interface, optimized for tablets and smartphones."
        },
        {
            icon: <WifiOutlined className="text-3xl text-[#289E80]" />,
            title: "Offline & Online Support",
            description: "Work anywhere, anytime. Sync your data when connected or continue working offline without interruption."
        },
        {
            icon: <DownloadOutlined className="text-3xl text-[#289E80]" />,
            title: "PDF Report Generation",
            description: "Generate professional inspection reports instantly and download them as PDF for easy sharing and archiving."
        },
        {
            icon: <TeamOutlined className="text-3xl text-[#289E80]" />,
            title: "Multi-User Management",
            description: "Manage multiple inspectors and crew members with role-based access control for your fleet operations."
        }
    ];

    const testimonials = [
        {
            name: "Captain James Morrison",
            role: "Fleet Manager, Atlantic Shipping Co.",
            avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
            rating: 5,
            text: "SpotFlash revolutionized our inspection process. The offline capability is a game-changer when we're out at sea with limited connectivity."
        },
        {
            name: "Maria Santos",
            role: "Marine Surveyor, Port Authority",
            avatar: "https://images.unsplash.com/photo-1494790108755-2616b9a0a77f?w=150&h=150&fit=crop&crop=face",
            rating: 5,
            text: "The mobile interface makes inspections so much faster. I can complete reports directly on my tablet and generate PDFs instantly."
        },
        {
            name: "Captain Erik Nielsen",
            role: "Owner, Nordic Maritime Group",
            avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
            rating: 5,
            text: "Managing our entire fleet with multiple inspectors has never been easier. The multi-user system keeps everyone organized."
        },
        {
            name: "Sarah Chen",
            role: "Marine Engineer, Pacific Fleet",
            avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
            rating: 5,
            text: "The customization options allow us to tailor inspections to our specific vessel types. Outstanding flexibility and reliability."
        }
    ];

    const plans = [
        {
            name: "Single Inspector",
            price: "$49",
            period: "/month",
            description: "Perfect for individual inspectors or small operations",
            features: [
                "1 Inspector account",
                "Up to 3 vessels",
                "Offline inspection support",
                "PDF report generation",
                "Mobile app access",
                "Basic customization"
            ],
            icon: <UserOutlined />,
            popular: false,
            gradient: "from-[#289E80] to-[#1f7a63]"
        },
        {
            name: "Fleet Pro",
            price: "$149",
            period: "/month",
            description: "Ideal for fleet owners and maritime companies",
            features: [
                "Up to 10 inspectors",
                "Unlimited vessels",
                "Advanced customization",
                "Multi-user dashboard",
                "Priority support",
                "API access",
                "Advanced reporting"
            ],
            icon: <CrownOutlined />,
            popular: true,
            gradient: "from-[#289E80] to-[#1a6b56]"
        },
        {
            name: "Enterprise",
            price: "$399",
            period: "/month",
            description: "For large maritime organizations",
            features: [
                "Unlimited inspectors",
                "Unlimited vessels",
                "Custom integrations",
                "Dedicated account manager",
                "24/7 phone support",
                "SLA guarantee",
                "White-label options",
                "Advanced analytics"
            ],
            icon: <FireOutlined />,
            popular: false,
            gradient: "from-[#289E80] to-[#0d4a3d]"
        }
    ];

    const feedbacks = [
        { rating: 5, count: 847, label: "Excellent" },
        { rating: 4, count: 223, label: "Very Good" },
        { rating: 3, count: 45, label: "Average" },
        { rating: 2, count: 12, label: "Poor" },
        { rating: 1, count: 3, label: "Terrible" }
    ];

    const totalReviews = feedbacks.reduce((sum, feedback) => sum + feedback.count, 0);
    const averageRating = (
        feedbacks.reduce((sum, feedback) => sum + feedback.rating * feedback.count, 0) / totalReviews
    ).toFixed(1);

    const handleFeedbackSubmit = (values) => {
        toast.success('Thank you for your feedback! We appreciate your input.');
        feedbackForm.resetFields();
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
            {/* Hero Section */}
            <div id="hero" className="relative overflow-hidden  bg-gradient-to-r from-[#0087af] to-[#00ae68] min-h-screen flex items-center">
                <div className="absolute inset-0 bg-black opacity-10"></div>
                <div className="absolute inset-0">
                    <div className="absolute top-20 left-20 w-72 h-72 bg-emerald-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                    <div className="absolute top-40 right-20 w-72 h-72 bg-teal-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
                    <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-green-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-4000"></div>
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
                    <div className="space-y-8 animate-fade-in">

                        <Title level={1} className="!text-white !text-5xl lg:!text-7xl !font-bold !leading-tight">
                            Smart Ship Inspections
                            <span className="block text-emerald-200">Made Simple</span>
                        </Title>
                        <Paragraph className="!text-xl lg:!text-2xl !text-emerald-100 max-w-3xl mx-auto !leading-relaxed">
                            Revolutionize your maritime inspections with mobile-first technology, offline support,
                            and instant PDF reporting. Trusted by ship owners and marine surveyors worldwide.
                        </Paragraph>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
                            <Button
                                type="primary"
                                size="large"
                                onClick={()=>navigate("/verify")}
                                className="!bg-white !text-[#289E80] !rounded-4xl !border-none !h-14 !px-8 !text-lg !font-semibold hover:!scale-105 transition-transform duration-200"
                                icon={<RocketOutlined />}
                            >
                                Start Free Trial
                            </Button>
                            <Button
                                size="large"
                                className="!bg-white !bg-opacity-10 !rounded-4xl  !text-gray-500 !border-white !h-14 !px-8 !text-lg hover:!bg-opacity-20 backdrop-blur-sm"
                            >
                                Schedule Demo
                            </Button>
                        </div>
                        <div className="flex justify-center items-center space-x-8 pt-12 text-emerald-200">
                            <div className="text-center">
                                <div className="text-2xl font-bold">10+</div>
                                <div className="text-sm">Vessels Inspected</div>
                            </div>


                        </div>
                    </div>
                </div>
            </div>

            {/* Features Section */}
            <div id="features" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <Title level={2} className="!text-5xl !font-bold !mb-4 !text-gray-800">
                            Built for Maritime Professionals
                        </Title>
                        <Paragraph className="!text-xl !text-gray-600 max-w-2xl mx-auto">
                            Everything you need for efficient ship inspections, from mobile optimization to offline capabilities.
                        </Paragraph>
                    </div>

                    <Row gutter={[32, 32]}>
                        {features.map((feature, index) => (
                            <Col xs={24} md={12} lg={6} key={index}>
                                <Card
                                    className="!h-full !border-0 !shadow-lg hover:!shadow-2xl transition-all duration-300 hover:!transform hover:!-translate-y-2 !bg-gradient-to-br !from-white !to-emerald-50"
                                    bodyStyle={{ padding: '32px' }}
                                >
                                    <div className="text-center space-y-4">
                                        <div className="mx-auto w-16 h-16 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center mb-6">
                                            {feature.icon}
                                        </div>
                                        <Title level={4} className="!text-xl !font-bold !mb-3">
                                            {feature.title}
                                        </Title>
                                        <Paragraph className="!text-gray-600 !leading-relaxed">
                                            {feature.description}
                                        </Paragraph>
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>
            </div>

            {/* Testimonials Section with Carousel */}
            {/* <div id="testimonials" className="py-24 bg-gradient-to-r from-emerald-50 to-teal-50">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <Title level={2} className="!text-5xl !font-bold !mb-4">
                            Trusted by Maritime Professionals
                        </Title>
                        <Paragraph className="!text-xl !text-gray-600 max-w-2xl mx-auto">
                            See what ship owners, inspectors, and marine surveyors are saying about SpotFlash.
                        </Paragraph>
                    </div>

                    <Carousel
                        autoplay
                        effect="fade"
                        dots={true}
                        autoplaySpeed={4000}
                        speed={1000}
                        className="testimonial-carousel"
                    >
                        {testimonials.map((testimonial, index) => (
                            <div key={index}>
                                <Card
                                    className="!border-0 !shadow-xl mx-auto max-w-4xl !bg-white hover:!shadow-2xl transition-shadow duration-300"
                                    bodyStyle={{ padding: '48px' }}
                                >
                                    <div className="text-center space-y-6">
                                        <Avatar
                                            size={80}
                                            src={testimonial.avatar}
                                            icon={<UserOutlined />}
                                            className="!border-4 !border-[#289E80] mx-auto shadow-lg"
                                        />
                                        <Rate disabled defaultValue={testimonial.rating} className="!text-yellow-400 !text-xl" />
                                        <Paragraph className="!text-gray-700 !text-xl !leading-relaxed !italic max-w-3xl mx-auto">
                                            "{testimonial.text}"
                                        </Paragraph>
                                        <div className="space-y-2">
                                            <div className="font-semibold text-gray-900 text-lg">{testimonial.name}</div>
                                            <div className="text-[#289E80] font-medium">{testimonial.role}</div>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        ))}
                    </Carousel>
                </div>
            </div> */}

            {/* Plans Section */}
            <div id="pricing" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <Title level={2} className="!text-5xl !font-bold !mb-4">
                            Choose Your Subscription
                        </Title>
                        <Paragraph className="!text-xl !text-gray-600 max-w-2xl mx-auto">
                            Scale your inspection operations with our flexible plans. Manage multiple users and vessels based on your needs.
                        </Paragraph>
                    </div>

                    <Row gutter={[32, 32]} justify="center">
                        {plans.map((plan, index) => (
                            <Col xs={24} lg={8} key={index}>
                                <Card
                                    className={`!h-full !border-0 !shadow-xl hover:!shadow-2xl transition-all duration-300 hover:!transform hover:!scale-105 relative overflow-hidden ${plan.popular ? '!ring-2 !ring-[#289E80]' : ''
                                        }`}
                                    bodyStyle={{ padding: '0' }}
                                    onMouseEnter={() => setHoveredPlan(index)}
                                    onMouseLeave={() => setHoveredPlan(null)}
                                >
                                    {plan.popular && (
                                        <div className="absolute top-0 right-0 bg-gradient-to-r from-[#289E80] to-[#1f7a63] text-white px-4 py-2 text-sm font-semibold rounded-bl-lg">
                                            Most Popular
                                        </div>
                                    )}

                                    <div className={` bg-gradient-to-r from-[#0087af] to-[#00ae68]  p-8 text-white text-center`}>
                                        <div className="text-3xl mb-4">{plan.icon}</div>
                                        <Title level={3} className="!text-white !mb-2">{plan.name}</Title>
                                        <div className="flex items-baseline justify-center mb-4">
                                            <span className="text-5xl font-bold">{plan.price}</span>
                                            <span className="text-xl ml-2 opacity-80">{plan.period}</span>
                                        </div>
                                        <Paragraph className="!text-white !opacity-90 !mb-0">
                                            {plan.description}
                                        </Paragraph>
                                    </div>

                                    <div className="p-8">
                                        <div className="space-y-4 mb-8">
                                            {plan.features.map((feature, featureIndex) => (
                                                <div key={featureIndex} className="flex items-center space-x-3">
                                                    <CheckCircleOutlined className="text-[#289E80] text-lg flex-shrink-0" />
                                                    <Text className="text-gray-700">{feature}</Text>
                                                </div>
                                            ))}
                                        </div>

                                        <InspectionBtn
                                            type={plan.popular ? "primary" : "default"}
                                            size="large"
                                            block
                                            onClick={() => navigate("/verify")}

                                        >
                                            Get Started
                                        </InspectionBtn>
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>
            </div>

            {/* Feedback Collection Section */}
            <div id="contact" className="py-24 bg-gradient-to-r from-emerald-50 to-teal-50">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <Row gutter={[48, 48]} align="middle">
                        <Col xs={24} lg={12}>
                            <div className="space-y-6">
                                <Title level={2} className="!text-4xl !font-bold !mb-6">
                                    Share Your Experience
                                </Title>
                                <Paragraph className="!text-lg !text-gray-600 !mb-8">
                                    Help us improve SpotFlash by sharing your feedback. Your insights help us build better tools for the maritime industry.
                                </Paragraph>

                                <Form form={feedbackForm} onFinish={handleFeedbackSubmit} layout="vertical">
                                    <Form.Item
                                        name="name"
                                        label="Your Name"
                                        rules={[{ required: true, message: 'Please enter your name' }]}
                                    >
                                        <Input size="large" placeholder="Enter your name" />
                                    </Form.Item>

                                    <Form.Item
                                        name="company"
                                        label="Company/Organization"
                                    >
                                        <Input size="large" placeholder="Enter your company name" />
                                    </Form.Item>

                                    <Form.Item
                                        name="rating"
                                        label="Overall Rating"
                                        rules={[{ required: true, message: 'Please provide a rating' }]}
                                    >
                                        <Rate className="!text-2xl" />
                                    </Form.Item>

                                    <Form.Item
                                        name="feedback"
                                        label="Your Feedback"
                                        rules={[{ required: true, message: 'Please share your feedback' }]}
                                    >
                                        <TextArea
                                            rows={4}
                                            placeholder="Tell us about your experience with SpotFlash..."
                                            size="large"
                                        />
                                    </Form.Item>

                                    <Form.Item>
                                        <InspectionBtn
                                            type="primary"
                                            htmlType="submit"
                                            size="large"
                                            className="!bg-[#289E80] !border-[#289E80] hover:!bg-[#1f7a63]"
                                            icon={<SendOutlined />}
                                        >
                                            Submit Feedback
                                        </InspectionBtn>
                                    </Form.Item>
                                </Form>
                            </div>
                        </Col>

                        <Col xs={24} lg={12}>
                            <Card className="!border-0 !shadow-xl" bodyStyle={{ padding: '48px' }}>
                                <div className="text-center mb-8">
                                    <Title level={3} className="!text-2xl !font-bold !mb-4">
                                        Customer Satisfaction
                                    </Title>
                                    <div className="text-6xl font-bold text-[#289E80] mb-4">{averageRating}</div>
                                    <Rate disabled defaultValue={Math.round(parseFloat(averageRating))} className="!text-2xl !text-yellow-400 mb-4" />
                                    <Paragraph className="!text-lg !text-gray-600 !mb-0">
                                        Based on {totalReviews.toLocaleString()} reviews
                                    </Paragraph>
                                </div>

                                <div className="space-y-4">
                                    {feedbacks.map((feedback, index) => (
                                        <div key={index} className="flex items-center space-x-4">
                                            <div className="flex items-center space-x-2 w-20">
                                                <span className="text-sm font-medium">{feedback.rating}</span>
                                                <StarOutlined className="text-yellow-400" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="bg-gray-200 rounded-full h-3 relative overflow-hidden">
                                                    <div
                                                        className="bg-gradient-to-r from-[#289E80] to-[#1f7a63] h-full rounded-full transition-all duration-1000"
                                                        style={{ width: `${(feedback.count / Math.max(...feedbacks.map(f => f.count))) * 100}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                            <div className="w-16 text-right">
                                                <span className="text-sm text-gray-600">{feedback.count}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Footer CTA */}
            <div className="py-24 bg-gradient-to-r from-[#289E80] to-[#1a6b56]">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
                    <Title level={2} className="!text-white !text-5xl !font-bold !mb-6">
                        Ready to Modernize Your Inspections?
                    </Title>
                    <Paragraph className="!text-xl !text-emerald-100 !mb-8 max-w-2xl mx-auto">
                        Join maritime professionals worldwide who trust SpotFlash for efficient, reliable ship inspections.
                    </Paragraph>
                    <Space size="large" className="flex justify-center">
                        <InspectionBtn
                            type="primary"
                            size="large"
                            className="!bg-white !flash-color !border-none !h-14 !px-8 !text-lg !font-semibold hover:!scale-105 transition-transform duration-200"
                            icon={<RocketOutlined />}
                        >
                            Start Your Free Trial
                        </InspectionBtn>
                        <Button
                            size="large"
                            className="!bg-transparent !text-white !border-white !h-14 !px-8 !text-lg hover:!bg-white hover:!text-[#289E80]"
                        >
                            Contact Sales
                        </Button>
                    </Space>
                </div>
            </div>


        </div>
    );
};

export default SpotFlashLanding;